// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { downloadFile } from '@shared/data-access/utils';

/**
 * Standalone bulk-upload template entities (files under ui-kit `/docs`).
 * Amortizations ship inside the debts workbook — not a separate download.
 */
export type BankUploadTemplateEntity =
  | 'accounts'
  | 'positions'
  | 'transactions'
  | 'debts';

export type BankUploadTemplateLocale = 'en' | 'es';

/** Public path mirror of `libs/shared/ui-kit/src/assets/docs/` (webpack copy). */
const BANK_UPLOAD_TEMPLATE_DOCS_BASE = '/docs';

/**
 * User-facing filenames (no `*.en.xlsx` / `*.es.xlsx` infix). Keep in sync with
 * `TEMPLATE_FILENAME_BY_ENTITY` in `tools/scripts/generate-bank-templates.mjs`.
 */
const BANK_UPLOAD_TEMPLATE_FILENAME: Record<
  BankUploadTemplateEntity,
  Record<BankUploadTemplateLocale, string>
> = {
  accounts: { en: 'accounts.xlsx', es: 'cuentas.xlsx' },
  positions: { en: 'positions.xlsx', es: 'posiciones.xlsx' },
  transactions: { en: 'transactions.xlsx', es: 'movimientos.xlsx' },
  debts: { en: 'debts.xlsx', es: 'deudas.xlsx' },
};

export const resolveBankUploadTemplateLocale = (
  language: string | undefined,
): BankUploadTemplateLocale =>
  (language ?? '').toLowerCase().startsWith('es') ? 'es' : 'en';

export const buildBankUploadTemplateFilename = (
  entity: BankUploadTemplateEntity,
  locale: BankUploadTemplateLocale,
): string => BANK_UPLOAD_TEMPLATE_FILENAME[entity][locale];

export const buildBankUploadTemplateUrl = (
  entity: BankUploadTemplateEntity,
  locale: BankUploadTemplateLocale,
): string =>
  `${BANK_UPLOAD_TEMPLATE_DOCS_BASE}/${buildBankUploadTemplateFilename(
    entity,
    locale,
  )}`;

/**
 * Triggers download of the official Citec bank-upload `.xlsx` for the given entity
 * and UI language (localized short filename under `/docs`).
 *
 * Fetches into a blob first so Chromium downloads via `blob:` instead of a
 * navigational HTTPS download (avoids “Network issue” on self-signed/dev certs).
 */
export const downloadBankUploadTemplate = async (
  entity: BankUploadTemplateEntity,
  language?: string,
): Promise<void> => {
  const locale = resolveBankUploadTemplateLocale(language);

  const filename = buildBankUploadTemplateFilename(entity, locale);

  const url = buildBankUploadTemplateUrl(entity, locale);

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Failed to download bank upload template (${response.status}): ${url}`,
    );
  }

  const blob = await response.blob();

  const objectUrl = URL.createObjectURL(blob);

  try {
    downloadFile({
      url: objectUrl,
      filename,
    });
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1_000);
  }
};
